import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type {
  ProcessingTask,
  TaskProcessingState,
  TaskProcessingStore,
  TaskStatusResponse,
} from './taskProcessing.types';
import type { NormalizedTaskStatus } from '../lib/schemas/task.schema';
import { normalizeTaskStatus } from '../lib/schemas/task.schema';
import { isTaskStatus, taskStatuses } from '../types/tasks';
import type { TaskStatus } from '../types/tasks';
import type { OcrTaskStartResponse } from '../types/api';
import { parseTaskType } from '../lib/schemas/task.schema';

const STORAGE_KEY = 'async-processing-tasks';
const LEGACY_STORAGE_KEY = 'ocr-processing-tasks';

const deriveCategory = (
  status: NormalizedTaskStatus,
): ProcessingTask['category'] => status;

const legacyStatusToTaskStatus: Record<string, TaskStatus> = {
  processing: 'STARTED',
  completed: 'SUCCESS',
  failed: 'FAILURE',
};

const fallbackTaskStatus: TaskStatus = taskStatuses[1]; // STARTED

const mapStatusToTaskStatus = (status: string | undefined): TaskStatus => {
  if (!status) {
    return fallbackTaskStatus;
  }

  if (isTaskStatus(status)) {
    return status;
  }

  const fallback = legacyStatusToTaskStatus[status];
  if (fallback) {
    return fallback;
  }

  return fallbackTaskStatus;
};

const legacyStatusToNormalized: Record<string, NormalizedTaskStatus> = {
  processing: 'processing',
  completed: 'success',
  failed: 'failure',
};

interface LegacyProcessingTask {
  id?: string;
  type?: string;
  filename?: string;
  startedAt?: string;
  status?: string;
  resultId?: number | null;
  errorMessage?: string | null;
  completedAt?: string | null;
}

interface LegacyMigrationResult {
  tasks: Record<string, ProcessingTask>;
  order: string[];
  lastHydratedAt: number;
}

const migrateLegacyTasks = (): LegacyMigrationResult | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      return null;
    }

    const tasks: Record<string, ProcessingTask> = {};
    const order: string[] = [];

    parsed.forEach((item: LegacyProcessingTask, index) => {
      const legacyStatus = item?.status ?? 'processing';
      const normalized = legacyStatusToNormalized[legacyStatus] ?? 'processing';
      const mappedStatus = legacyStatusToTaskStatus[legacyStatus] ?? 'STARTED';

      const startedAt = item?.startedAt
        ? Date.parse(item.startedAt)
        : undefined;
      const completedAt = item?.completedAt
        ? Date.parse(item.completedAt)
        : undefined;

      const id =
        typeof item?.id === 'string' && item.id.length > 0
          ? item.id
          : `legacy-${index}-${startedAt ?? Date.now()}`;

      if (tasks[id]) {
        return;
      }

      const responseLike: TaskStatusResponse = {
        taskId: id,
        type: item?.type === 'sales' ? 'ocr.sales' : 'ocr.invoice',
        taskType: item?.type === 'sales' ? 'ocr.sales' : 'ocr.invoice',
        taskSubtype: item?.type === 'sales' ? 'sales' : 'invoice',
        status: mapStatusToTaskStatus(mappedStatus),
        normalizedStatus: normalized,
        resultId: item?.resultId ?? null,
        error: item?.errorMessage ?? null,
        details:
          typeof item?.filename === 'string'
            ? { filename: item.filename }
            : undefined,
        queuedAt: undefined,
        startedAt: startedAt ? new Date(startedAt).toISOString() : undefined,
        completedAt: completedAt
          ? new Date(completedAt).toISOString()
          : undefined,
      };

      tasks[id] = mapTaskStatusResponseToProcessingTask(responseLike);

      order.push(id);
    });

    window.localStorage.removeItem(LEGACY_STORAGE_KEY);

    if (order.length === 0) {
      return null;
    }

    return {
      tasks,
      order,
      lastHydratedAt: Date.now(),
    } satisfies LegacyMigrationResult;
  } catch (error) {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    return null;
  }
};

const emptyState: TaskProcessingState = {
  tasks: {},
  order: [],
  lastHydratedAt: null,
};

const createInitialState = (): TaskProcessingState => {
  if (typeof window !== 'undefined') {
    const migrated = migrateLegacyTasks();
    if (migrated) {
      return migrated;
    }
  }

  return { ...emptyState };
};

const createStore = (
  set: (updater: (state: TaskProcessingState) => TaskProcessingState) => void,
  _get: () => TaskProcessingState,
): TaskProcessingStore => ({
  ...createInitialState(),

  startTask: (task) => {
    set((state) => {
      const nextTasks = { ...state.tasks, [task.id]: task };
      const nextOrder = state.order.includes(task.id)
        ? state.order
        : [...state.order, task.id];
      return {
        tasks: nextTasks,
        order: nextOrder,
        lastHydratedAt: state.lastHydratedAt ?? Date.now(),
      } satisfies TaskProcessingState;
    });
  },

  updateTaskStatus: (taskId, update) => {
    set((state) => {
      const existing = state.tasks[taskId];
      if (!existing) {
        return state;
      }

      const timestamps = {
        ...existing.timestamps,
        ...(update.timestamps ?? {}),
      };

      const nextStatus = update.status ?? existing.status;
      const nextNormalizedStatus =
        update.normalizedStatus ?? existing.normalizedStatus;

      const nextCategory =
        update.category ?? deriveCategory(nextNormalizedStatus);

      const nextTask: ProcessingTask = {
        ...existing,
        ...update,
        timestamps,
        status: nextStatus,
        normalizedStatus: nextNormalizedStatus,
        category: nextCategory,
      };

      return {
        tasks: {
          ...state.tasks,
          [taskId]: nextTask,
        },
        order: state.order,
        lastHydratedAt: Date.now(),
      } satisfies TaskProcessingState;
    });
  },

  removeTask: (taskId) => {
    set((state) => {
      if (!state.tasks[taskId]) {
        return state;
      }
      const nextTasks = { ...state.tasks };
      delete nextTasks[taskId];
      return {
        tasks: nextTasks,
        order: state.order.filter((id) => id !== taskId),
        lastHydratedAt: Date.now(),
      } satisfies TaskProcessingState;
    });
  },

  hydrateTasks: (payload) => {
    set((state) => {
      if (payload.length === 0) {
        return {
          ...state,
          lastHydratedAt: Date.now(),
        } satisfies TaskProcessingState;
      }

      const incomingEntries = payload.map(
        mapTaskStatusResponseToProcessingTask,
      );

      const nextTasks: Record<string, ProcessingTask> = {
        ...state.tasks,
      };
      const incomingOrder: string[] = [];

      incomingEntries.forEach((task) => {
        nextTasks[task.id] = task;
        incomingOrder.push(task.id);
      });

      const nextOrder = Array.from(new Set([...incomingOrder, ...state.order]));

      return {
        tasks: nextTasks,
        order: nextOrder,
        lastHydratedAt: Date.now(),
      } satisfies TaskProcessingState;
    });
  },

  clearAll: () => {
    set(() => ({ ...emptyState }));
  },

  mergeTaskStatusResponse: (payload) => {
    set((state) => {
      const mapped = mapTaskStatusResponseToProcessingTask(payload);
      const existing = state.tasks[payload.taskId];

      if (existing && areProcessingTasksEqual(existing, mapped)) {
        return state;
      }

      const nextTasks: Record<string, ProcessingTask> = {
        ...state.tasks,
        [payload.taskId]: existing
          ? {
              ...existing,
              ...mapped,
              details: mapped.details ?? existing.details,
              timestamps: {
                ...existing.timestamps,
                ...mapped.timestamps,
              },
            }
          : mapped,
      };

      const orderIncludes = state.order.includes(payload.taskId);

      return {
        tasks: nextTasks,
        order: orderIncludes ? state.order : [...state.order, payload.taskId],
        lastHydratedAt: Date.now(),
      } satisfies TaskProcessingState;
    });
  },
});

export const useTaskProcessingStore = create<TaskProcessingStore>()(
  persist(createStore, {
    name: STORAGE_KEY,
    version: 1,
    partialize: (state) => ({
      tasks: state.tasks,
      order: state.order,
      lastHydratedAt: state.lastHydratedAt,
    }),
  }),
);

let cachedProcessingOrder: TaskProcessingState['order'] | null = null;
let cachedProcessingTasksMap: TaskProcessingState['tasks'] | null = null;
let cachedProcessingTasks: ProcessingTask[] = [];

export const selectProcessingTasks = (state: TaskProcessingState) => {
  if (
    cachedProcessingOrder === state.order &&
    cachedProcessingTasksMap === state.tasks
  ) {
    return cachedProcessingTasks;
  }

  const nextTasks = state.order
    .map((id) => state.tasks[id])
    .filter((task): task is ProcessingTask => Boolean(task));

  cachedProcessingOrder = state.order;
  cachedProcessingTasksMap = state.tasks;
  cachedProcessingTasks = nextTasks;

  return cachedProcessingTasks;
};

export const selectActiveTaskIds = (state: TaskProcessingState) => state.order;

export const selectClearAll = (state: TaskProcessingStore) => state.clearAll;

let cachedPendingSource: ProcessingTask[] | null = null;
let cachedPendingTasks: ProcessingTask[] = [];

export const selectPendingProcessingTasks = (state: TaskProcessingState) => {
  const tasks = selectProcessingTasks(state);

  if (cachedPendingSource === tasks) {
    return cachedPendingTasks;
  }

  const nextPending = tasks.filter((task) =>
    ['queued', 'processing', 'retrying'].includes(task.normalizedStatus),
  );

  cachedPendingSource = tasks;
  cachedPendingTasks = nextPending;

  return cachedPendingTasks;
};

export const isTaskTerminal = (task: ProcessingTask) =>
  ['success', 'failure', 'canceled'].includes(task.normalizedStatus);

export const mapTaskStatusResponseToProcessingTask = (
  response: TaskStatusResponse,
): ProcessingTask => {
  const normalizedStatus = normalizeTaskStatus(response.status);
  return {
    id: response.taskId,
    type: response.taskType,
    subtype: response.taskSubtype,
    status: mapStatusToTaskStatus(response.status),
    normalizedStatus,
    category: deriveCategory(normalizedStatus),
    resultId: response.resultId ?? null,
    error: response.error ?? null,
    details: response.details ?? undefined,
    timestamps: {
      queuedAt: response.queuedAt ?? null,
      startedAt: response.startedAt ?? null,
      completedAt: response.completedAt ?? null,
    },
  } satisfies ProcessingTask;
};

export const createProcessingTaskFromStartResponse = (
  response: OcrTaskStartResponse,
  filename?: string,
): ProcessingTask => {
  // The start endpoint returns a bare subtype (e.g. "invoice"), whereas the
  // status endpoint returns the dot-delimited "ocr.invoice". Normalize here so
  // the task is categorized correctly (and matched by invoice filters) right
  // away, instead of falling back to "unknown" until the first status poll.
  const normalizedType = response.taskType.includes('.')
    ? response.taskType
    : `ocr.${response.taskType}`;
  const { taskType, taskSubtype } = parseTaskType(normalizedType);
  const normalizedStatus = normalizeTaskStatus(response.status);

  return {
    id: response.taskId,
    type: taskType,
    subtype: taskSubtype,
    status: mapStatusToTaskStatus(response.status),
    normalizedStatus,
    category: deriveCategory(normalizedStatus),
    resultId: null,
    error: null,
    details: filename ? { filename } : undefined,
    timestamps: {
      queuedAt: null,
      startedAt: new Date().toISOString(),
      completedAt: null,
    },
  } satisfies ProcessingTask;
};

export type { TaskProcessingState } from './taskProcessing.types';

// Auto-remove completed/failed tasks after 30 seconds
export const TERMINAL_TASK_TTL_MS = 30000;

function areProcessingTasksEqual(
  a: ProcessingTask,
  b: ProcessingTask,
): boolean {
  if (
    a === b ||
    (a.status === b.status &&
      a.normalizedStatus === b.normalizedStatus &&
      a.category === b.category &&
      a.resultId === b.resultId &&
      a.error === b.error &&
      areDetailsEqual(a.details, b.details) &&
      areTimestampsEqual(a.timestamps, b.timestamps))
  ) {
    return true;
  }

  return false;
}

function areDetailsEqual(
  a: ProcessingTask['details'],
  b: ProcessingTask['details'],
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

function areTimestampsEqual(
  a: ProcessingTask['timestamps'],
  b: ProcessingTask['timestamps'],
): boolean {
  return (
    a.queuedAt === b.queuedAt &&
    a.startedAt === b.startedAt &&
    a.completedAt === b.completedAt
  );
}
